import React from 'react';
import { t } from 'i18next';
import compact from 'lodash/compact';
import find from 'lodash/find';
import upperCase from 'lodash/upperCase';
import { Form, Input, InputProps, message, Skeleton } from 'antd';

import { DeviceType } from '@/constants/deviceTypeEnum';
import { CompressorParameter } from '@/store/Engine/common';
import { maxLength } from '@/utils/util-functions';

export interface CompressorParametersTableProps {
  compressorParameters: CompressorParameter[];
  activeTab: string;
  setIsSaveDisabled: (value: boolean) => void;
  isHistory?: boolean;
}

export const compressorTypesAbbreviations = {
  [DeviceType.CentrifugalAndPowerSensor]: 'CPS',
};

export const generateInitialValues = (params: CompressorParameter[]) => {
  if (!params) return {};
  return params.reduce(
    (acc, param) => ({
      ...acc,
      [`${param.key}_priority`]: param.priority !== undefined ? param.priority : undefined,
      [`${param.key}_maxTarget`]: param.maxTarget || undefined,
      [`${param.key}_minTarget`]: param.minTarget || undefined,
      [`${param.key}_stopDelayMinutes`]: param.stopDelayMinutes || undefined,
      [`${param.key}_maxFlow`]: param.maxFlow || undefined,
      [`${param.key}_maxLoadAmps`]: param.maxLoadAmps || undefined,
      [`${param.key}_fiveLoop`]: param.fiveLoop || undefined,
    }),
    {},
  );
};

export const isFormComplete = (
  compressorParameters: CompressorParameter[],
  values: Record<string, string | number | undefined>,
) => {
  return compressorParameters?.every(param => {
    const fields = [
      values[`${param.key}_priority`],
      values[`${param.key}_maxTarget`],
      values[`${param.key}_minTarget`],
      values[`${param.key}_stopDelayMinutes`],
    ];
    return fields.every(val => val !== undefined && val !== '' && val !== null);
  });
};

export const validateField = (
  fieldName: string,
  value: string | number | undefined,
  localValues: Record<string, string | number | undefined>,
  compressorParameters: CompressorParameter[],
) => {
  const [key, field] = fieldName.split('_');
  const parsedValue = value === '' ? undefined : value;

  if (field === 'stopDelayMinutes' && (parsedValue === undefined || parsedValue === null || parsedValue === '')) {
    return t('stopDelayMinutesEmptyMessage');
  }

  if (field === 'priority') {
    if (parsedValue === undefined || parsedValue === null || parsedValue === '') {
      return t('priorityEmptyMessage');
    }

    const currentPriority = typeof parsedValue === 'string' ? parseInt(parsedValue, 10) : parsedValue;
    const hasDuplicatePriority = compressorParameters?.some(param => {
      if (param.key === key) return false;
      const otherPriority = localValues[`${param.key}_priority`];
      return otherPriority !== undefined && Number(otherPriority) === Number(currentPriority);
    });

    if (hasDuplicatePriority) {
      return t('priorityUniqueValueMessage');
    }
  }

  if (
    (field === 'maxTarget' || field === 'minTarget') &&
    (parsedValue === undefined || parsedValue === null || parsedValue === '')
  ) {
    return `${field === 'maxTarget' ? t('maxTarget') : t('minTarget')} ${t('shouldNotBeEmpty')}`;
  }

  if (field === 'maxTarget') {
    const minTarget = parseFloat(String(localValues[`${key}_minTarget`]));
    if (parsedValue !== undefined && minTarget !== undefined && Number(parsedValue) <= minTarget) {
      return t('maxTargetGreaterThanMinTarget');
    }
  }

  if (field === 'minTarget') {
    const maxTarget = parseFloat(String(localValues[`${key}_maxTarget`]));
    if (parsedValue !== undefined && maxTarget !== undefined && Number(parsedValue) >= maxTarget) {
      return t('minTargetLessThanMaxTarget');
    }
  }

  return '';
};

export const validateAllFields = (
  compressorParameters: CompressorParameter[],
  values: Record<string, string | number | undefined>,
  validateFieldFn: (fieldName: string, value: string | number | undefined) => string,
) => {
  const errors: Record<string, string> = {};
  let hasErrors = false;

  compressorParameters?.forEach(param => {
    const fields = ['priority', 'maxTarget', 'minTarget', 'stopDelayMinutes'];
    fields.forEach(field => {
      const fieldKey = `${param.key}_${field}`;
      const errorMessage = validateFieldFn(fieldKey, values[fieldKey]);
      if (errorMessage) {
        errors[fieldKey] = errorMessage;
        hasErrors = true;
      }
    });
  });

  return { errors, hasErrors };
};

export const getTableColumns = (
  compressors,
  compressorTypesAbbreviations: Record<string, string>,
  renderInput: (record: { key: string }, field: string, options?: InputProps) => JSX.Element,
  tunit: (text: string, unit: string) => string,
  unit: string,
  isLoading = false,
  isHistory = false,
) => [
  {
    title: 'No',
    dataIndex: 'deviceId',
    key: 'compressorNumber',
    render: (deviceId: string) => {
      if (isLoading) {
        return <Skeleton.Input active />;
      }
      const compressor = find(compressors, { id: deviceId });
      return compressor?.attributes?.compressorNumber || '';
    },
  },
  {
    dataIndex: 'deviceId',
    key: 'deviceId',
    title: 'Compressor',
    render: (deviceId: string, { compressorName }: { compressorName: string }) => {
      const compressor = find(compressors, { id: deviceId });
      if (isLoading) {
        return <Skeleton.Input active />;
      }
      return (
        <div>
          {compact([
            compressor?.name,
            compressor?.deviceModel?.modelSpecs?.hp,
            compressorTypesAbbreviations[compressor?.deviceModel?.deviceTypes?.[0]],
          ]).join(' | ') || compressorName}
        </div>
      );
    },
  },
  {
    title: t('priority'),
    dataIndex: 'priority',
    key: 'priority',
    render: (_, record) => {
      if (isLoading) {
        return <Skeleton.Input active />;
      }
      return renderInput(record, 'priority', {
        count: maxLength(2),
      });
    },
  },
  {
    title: t('minTarget'),
    dataIndex: 'minTarget',
    key: 'minTarget',
    render: (_, record) => {
      if (isLoading) {
        return <Skeleton.Input active />;
      }
      return renderInput(record, 'minTarget', {
        count: maxLength(4),
        suffix: tunit('Bar', upperCase(unit)),
      });
    },
  },
  {
    title: t('maxTarget'),
    dataIndex: 'maxTarget',
    key: 'maxTarget',
    render: (_, record) => {
      if (isLoading) {
        return <Skeleton.Input active />;
      }
      return renderInput(record, 'maxTarget', {
        count: maxLength(4),
        suffix: tunit('Bar', upperCase(unit)),
      });
    },
  },
  {
    title: t('stopDelayMinutes'),
    dataIndex: 'stopDelayMinutes',
    key: 'stopDelayMinutes',
    render: (_, record) => {
      if (isLoading) {
        return <Skeleton.Input active />;
      }
      return renderInput(record, 'stopDelayMinutes', {
        maxLength: 4,
        suffix: 'min',
      });
    },
  },
  ...(!isHistory
    ? [
        {
          title: t('maxFlow'),
          dataIndex: 'maxFlow',
          key: 'maxFlow',
          render: (_, record) =>
            renderInput(record, 'maxFlow', {
              maxLength: 4,
              disabled: true,
              suffix: tunit('m3/min', "METRIC"),
            }),
        },
        {
          title: t('maxLoadAmps'),
          dataIndex: 'maxLoadAmps',
          key: 'maxLoadAmps',
          render: (_, record) =>
            renderInput(record, 'maxLoadAmps', {
              maxLength: 4,
              disabled: true,
              suffix: 'amp',
            }),
        },
        {
          title: t('fiveLoop'),
          dataIndex: 'fiveLoop',
          key: 'fiveLoop',
          render: (_, record) => {
            return renderInput(record, 'fiveLoop', {
              disabled: true,
            });
          },
        },
      ]
    : []),
];

export const createRenderInputFunction = (
  fieldErrors: Record<string, string>,
  onInputScroll: (e: React.WheelEvent) => void,
  onInputFocus: (fieldName: string) => void,
  onInputBlur: () => void,
  activeTab: string,
) => {
  return (record: { key: string }, field: string, options: InputProps = {}) => {
    const fieldKey = `${record.key}_${field}`;
    const hasError = !!fieldErrors[fieldKey];

    return (
      <Form.Item name={fieldKey} validateStatus={hasError ? 'error' : undefined} style={{ marginBottom: 0 }}>
        <Input
          {...options}
          placeholder="+"
          onWheel={onInputScroll}
          bordered={hasError}
          onFocus={() => onInputFocus(fieldKey)}
          onBlur={onInputBlur}
          disabled={options?.disabled || activeTab === 'published'}
          className={hasError ? 'error-text' : ''}
        />
      </Form.Item>
    );
  };
};

export const showError = (content: string, setIsSaveDisabled: (value: boolean) => void) => {
  message.error({
    content,
    style: { marginTop: 'auto', marginBottom: 20 },
  });
  setIsSaveDisabled(true);
};

export const capitalize = (str?: string): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};
